/**
 * The role My ArtRouting is currently showing — Artist, Art Venue or Explorer.
 *
 * One account may hold several roles (chosen at /welcome). The active role
 * decides which profile, tools and allowances the profile pages load; the
 * paid level is shared across roles (Shared Tiers), so switching role never
 * changes what the account pays, only which role's profile is being edited.
 *
 * The choice is remembered per browser as a convenience; the account's roles
 * themselves come from GET /onboarding/status.
 */

import { create } from 'zustand';
import { api } from '@/lib/api';

export type AccountRole = 'artist' | 'gallery' | 'explorer';
const ROLES: AccountRole[] = ['artist', 'gallery', 'explorer'];
const KEY = 'artrouting.activeRole';

function remembered(): AccountRole | null {
  try {
    const v = localStorage.getItem(KEY) as AccountRole | null;
    return v && ROLES.includes(v) ? v : null;
  } catch { return null; }
}

interface ActiveRoleState {
  roles: AccountRole[];
  active: AccountRole;
  loaded: boolean;
  load: () => Promise<void>;
  setActive: (role: AccountRole) => void;
}

export const useActiveRoleStore = create<ActiveRoleState>((set, get) => ({
  roles: [],
  active: remembered() || 'artist',
  loaded: false,
  load: async () => {
    if (get().loaded) return;
    try {
      const s = await api.get<{ roles?: string[] }>('/onboarding/status');
      const roles = (s.roles || []).filter((r): r is AccountRole => ROLES.includes(r as AccountRole));
      const list = roles.length ? roles : ['artist' as AccountRole];
      const current = get().active;
      set({ roles: list, active: list.includes(current) ? current : list[0], loaded: true });
    } catch {
      set({ roles: ['artist'], loaded: true });
    }
  },
  setActive: (role) => {
    try { localStorage.setItem(KEY, role); } catch { /* private window */ }
    set({ active: role });
  },
}));
